import React from 'react';

// Enhanced error reporting utility
const createErrorReport = (error, errorInfo, additionalContext = {}) => {
  const report = {
    // Error details
    message: error?.message || 'Unknown error',
    name: error?.name || 'Error',
    stack: error?.stack || 'No stack trace available',
    
    // React-specific info
    componentStack: errorInfo?.componentStack || 'No component stack',
    
    // Context information
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    
    // Redux state (safely extracted)
    reduxState: (() => {
      try {
        // Try to get Redux state if available
        const store = window.__REDUX_STORE__ || additionalContext.store;
        if (store && store.getState) {
          const state = store.getState();
          return {
            auth: {
              isAuthenticated: state.auth?.isAuthenticated,
              loading: state.auth?.loading,
              error: state.auth?.error,
              userId: state.auth?.user?.id || 'unknown'
            },
            router: state.router || 'N/A'
          };
        }
        return 'Store not available';
      } catch (e) {
        return `Error accessing store: ${e.message}`;
      }
    })(),
    
    // Performance information
    memory: (() => {
      try {
        if (performance.memory) {
          return {
            used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
            total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
          };
        }
        return 'Memory info not available';
      } catch (e) {
        return 'Memory info error';
      }
    })(),
    
    // Additional context
    ...additionalContext
  };

  return report;
};

// Enhanced error boundary with better debugging
class EnhancedErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
      errorReport: null
    };
    
    // Bind methods
    this.handleRetry = this.handleRetry.bind(this);
    this.handleReload = this.handleReload.bind(this);
    this.downloadErrorReport = this.downloadErrorReport.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Generate unique error ID
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return { 
      hasError: true, 
      error,
      errorId
    };
  }

  componentDidCatch(error, errorInfo) {
    console.group('🔴 [Enhanced ErrorBoundary] Error Caught');
    console.warn('Error:', error);
    console.warn('Error Info:', errorInfo);
    
    // Create comprehensive error report
    const errorReport = createErrorReport(error, errorInfo, {
      errorId: this.state.errorId,
      retryCount: this.state.retryCount,
      props: this.props.debugInfo || {},
      store: this.props.store
    });

    console.table(errorReport);
    console.groupEnd();

    // Update state with error details
    this.setState({ 
      error, 
      errorInfo,
      errorReport
    });

    // Report to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportToService(errorReport);
    }

    // Also store in sessionStorage for debugging
    try {
      sessionStorage.setItem(`errorReport_${this.state.errorId}`, JSON.stringify(errorReport, null, 2));
    } catch (e) {
      console.warn('Could not save error report to sessionStorage');
    }
  }

  reportToService(errorReport) {
    // This is where you'd integrate with your error reporting service
    // Example: Sentry, LogRocket, Bugsnag, etc.
    
    try {
      // Placeholder for actual service integration
      if (window.Sentry) {
        window.Sentry.captureException(this.state.error, {
          contexts: {
            react: this.state.errorInfo,
            report: errorReport
          }
        });
      }
      
      // You could also send to your own API
      /*
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorReport)
      }).catch(e => console.warn('Failed to report error to API:', e));
      */
      
    } catch (reportingError) {
      console.warn('Failed to report error to service:', reportingError);
    }
  }

  handleRetry() {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorReport: null,
      retryCount: prevState.retryCount + 1
    }));
  }

  handleReload() {
    // Save current error report before reload
    if (this.state.errorReport) {
      try {
        localStorage.setItem('lastErrorBeforeReload', JSON.stringify(this.state.errorReport));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    window.location.reload();
  }

  downloadErrorReport() {
    if (!this.state.errorReport) return;

    try {
      const dataStr = JSON.stringify(this.state.errorReport, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `error-report-${this.state.errorId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('Failed to download error report:', e);
      // Fallback: copy to clipboard
      try {
        navigator.clipboard.writeText(JSON.stringify(this.state.errorReport, null, 2));
        alert('Error report copied to clipboard');
      } catch (clipboardError) {
        console.warn('Failed to copy to clipboard:', clipboardError);
      }
    }
  }

  renderErrorDetails() {
    if (process.env.NODE_ENV !== 'development' || !this.state.errorReport) {
      return null;
    }

    return (
      <div className="mt-6 p-4 bg-gray-100 rounded-lg">
        <details className="text-left">
          <summary className="font-semibold text-gray-700 cursor-pointer mb-2">
            🔍 Error Details (Development)
          </summary>
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-gray-800">Error Message:</h4>
              <p className="text-sm text-red-600 font-mono bg-red-50 p-2 rounded">
                {this.state.error?.message || 'Unknown error'}
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800">Component Stack:</h4>
              <pre className="text-xs text-gray-600 bg-gray-50 p-2 rounded overflow-auto max-h-32">
                {this.state.errorInfo?.componentStack || 'No component stack available'}
              </pre>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800">Redux State:</h4>
              <pre className="text-xs text-gray-600 bg-blue-50 p-2 rounded overflow-auto max-h-24">
                {JSON.stringify(this.state.errorReport.reduxState, null, 2)}
              </pre>
            </div>

            <button
              onClick={this.downloadErrorReport}
              className="mt-2 px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded"
            >
              📄 Download Full Report
            </button>
          </div>
        </details>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      const { error, retryCount, errorId } = this.state;
      const { fallback: CustomFallback } = this.props;

      // Use custom fallback if provided
      if (CustomFallback) {
        return (
          <CustomFallback
            error={error}
            errorInfo={this.state.errorInfo}
            errorReport={this.state.errorReport}
            retry={this.handleRetry}
            reload={this.handleReload}
            retryCount={retryCount}
            errorId={errorId}
          />
        );
      }

      // Default enhanced fallback UI
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.084 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Application Error
              </h2>
              <p className="text-gray-600 mb-2">
                Something unexpected happened. We apologize for the inconvenience.
              </p>
              {errorId && (
                <p className="text-xs text-gray-500 font-mono">
                  Error ID: {errorId}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                disabled={retryCount >= 3}
              >
                {retryCount >= 3 ? 'Max Retries Reached' : `Try Again ${retryCount > 0 ? `(${retryCount}/3)` : ''}`}
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Reload Page
              </button>
            </div>

            {/* Development Error Details */}
            {this.renderErrorDetails()}

            {/* Footer */}
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-2">
                If this problem persists, please contact support with the error ID above.
              </p>
              {process.env.NODE_ENV === 'development' && (
                <p className="text-xs text-blue-500">
                  💡 Check browser console for detailed logs
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default EnhancedErrorBoundary;